import * as Linking from 'expo-linking';
import { Redirect, usePathname } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { FullScreenSpinner, isWeb } from 'smoothie';

import { getAppScheme } from '../../../common/misc';
import { useStorage } from '../../../common/providers/StorageProvider';
import type { ContainerComponent } from '../../../common/types/Common';
import { getWebUrlWithoutProtocol } from '../../../lib/Web';
import { captureMessage } from '../../../logger/Logger';
import { useUser } from '../../../user/providers/UserProvider';
import { setStoredRedirectPath } from '../../helpers';

const RedirectIfNotAuthenticated: ContainerComponent = ({ children }) => {
    const storage = useStorage();
    const { user, loading: isLoadingUser } = useUser();
    const [initialUrl, setInitialUrl] = useState<string | null>(null);
    const [isLoadingInitialUrl, setIsLoadingInitialUrl] = useState(true);
    const expoPath = usePathname(); // Note: This does not include query parameters, so is only used as a fallback.

    useEffect(() => {
        const getUrl = async () => {
            try {
                const url = await Linking.getInitialURL();

                // The url, on native, might be an https url, app scheme url, or just a path.
                const pathOnNative = url?.includes(getWebUrlWithoutProtocol())
                    ? url?.split(getWebUrlWithoutProtocol())[1]
                    : url?.includes(`${getAppScheme()}://`)
                      ? url?.split(`${getAppScheme()}://`)[1]
                      : url;

                const initialUrl = isWeb
                    ? url?.split(window.origin)[1]
                    : pathOnNative;
                setInitialUrl(initialUrl ?? null);
            } catch (error) {
                console.error('Error getting initial URL:', error);
            } finally {
                setIsLoadingInitialUrl(false);
            }
        };

        void getUrl();
    }, []);

    // Wait until both the user and initial URL are loaded before we decide if we need to redirect.
    if (isLoadingUser || isLoadingInitialUrl) return <FullScreenSpinner />;

    let pathname = '';

    if (initialUrl != null) {
        pathname = initialUrl;
    } else {
        pathname = expoPath ?? '';
    }

    // Check to see if the user is fully authenticated.
    if (!user?.isFullyAuthed) {
        captureMessage(
            'User is not fully authenticated; Redirecting to sign in page.',
        );

        // If the user was following a (deep) link, but is not authenticated, store the link to redirect to after successful log in.
        setStoredRedirectPath(storage, pathname);

        // Redirect to log in page.
        return <Redirect href="/sign-in" />;
    }

    return <>{children}</>;
};

export default RedirectIfNotAuthenticated;
